import { BlogPermission } from '../../accounts/workspaces/permissions';
import type { ErrorCode } from '../../errors';
import type { PageQuery } from '../../paging';

// TASK-UC-1…6, ADR-019: фоновые задачи. Модель общая для всех видов: вид знает свои параметры и
// отчёт, а прогресс и состояния — одни на всех.

export enum TaskKind {
  /** MED-UC-9: пережатие исходников блога. */
  MediaRecompress = 'media.recompress',
  /** MED-UC-10: пересчёт объёма медиа — задача платформы. */
  MediaStorageRecount = 'media.storageRecount',
  /** OPS-UC-8: создание резервной копии — задача платформы. */
  Backup = 'operations.backup',
  /** OPS-UC-11: восстановление из резервной копии — задача платформы. */
  Restore = 'operations.restore',
}

export enum TaskStatus {
  Queued = 'queued',
  Running = 'running',
  /** TASK-UC-5: сбой системы, ждёт возобновления. */
  Paused = 'paused',
  Completed = 'completed',
  /** TASK-UC-5.CC-1: были ошибки отдельных шагов. */
  CompletedWithErrors = 'completedWithErrors',
  Cancelled = 'cancelled',
}

/** TASK-UC-1.CC-1: незавершённые задачи — их в блоге одна. */
export const ACTIVE_TASK_STATUSES: readonly TaskStatus[] = [TaskStatus.Queued, TaskStatus.Running, TaskStatus.Paused];

export function isTaskActive(status: TaskStatus): boolean {
  return ACTIVE_TASK_STATUSES.includes(status);
}

/** Текущий шаг: что делается и какая его доля готова (у долгого шага — видео). */
export interface TaskCurrentStepDto {
  /** Подпись шага (имя файла); null — у администратора вне блога (TASK-RULE-3). */
  label: string | null;
  /** 0…1; null — неизвестно. */
  fraction: number | null;
}

/** TASK-UC-2: прогресс в общих единицах — шаги и, если вид их знает, байты. */
export interface TaskProgressDto {
  total: number;
  done: number;
  failed: number;
  skipped: number;
  /** null — вид не считает объём. */
  bytesTotal: number | null;
  bytesDone: number | null;
  current: TaskCurrentStepDto | null;
}

export const EMPTY_TASK_PROGRESS: TaskProgressDto = { total: 0, done: 0, failed: 0, skipped: 0, bytesTotal: null, bytesDone: null, current: null };

export interface TaskActorDto {
  id: string;
  displayName: string;
}

/**
 * Фоновая задача. Параметры и отчёт — свои у каждого вида (для пережатия —
 * RecompressParamsDto и RecompressReportDto из content/media/recompress).
 */
export interface TaskDto<P = unknown, R = unknown> {
  id: string;
  kind: TaskKind;
  /** null — задача платформы. */
  blogId: string | null;
  blogName: string | null;
  status: TaskStatus;
  /** null — поставила система. */
  createdBy: TaskActorDto | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  updatedAt: string;
  /** TASK-UC-4: отмену попросили, текущий шаг прерывается. */
  cancelRequested: boolean;
  /** TASK-UC-5: причина приостановки. */
  pauseReason: ErrorCode | null;
  params: P;
  progress: TaskProgressDto;
  /** null — у администратора вне блога (TASK-RULE-3) или вид ещё ничего не сообщил. */
  report: R | null;
}

// TASK-UC-3, TASK-UC-6
export interface TaskListQuery extends PageQuery {
  status?: TaskStatus;
}

/** Сколько ошибок шагов хранит отчёт (ADR-019): счётчики точные, перечень — первые. */
export const TASK_REPORT_MAX_FAILURES = 100;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * TASK-RULE-3, ADM-RULE-2: задача для администратора вне блога — без содержимого блога. Подпись
 * шага (имя файла) снимается, а перечень ошибок по шагам (`failures` отчёта — соглашение для всех
 * видов) пустеет; счётчики и объёмы остаются.
 */
export function taskWithoutBlogContent<P, R>(task: TaskDto<P, R>): TaskDto<P, R> {
  if (task.blogId === null) return task;
  const current = task.progress.current ? { ...task.progress.current, label: null } : null;
  const report = isRecord(task.report) && Array.isArray(task.report.failures) ? ({ ...task.report, failures: [] } as R) : task.report;
  return { ...task, progress: { ...task.progress, current }, report };
}

/**
 * TASK-RULE-3: кто в блоге ставит, отменяет и возобновляет задачу вида; null — только
 * администратор (задачи платформы). Его же берёт интерфейс, чтобы прятать кнопки.
 */
export function taskManagePermission(kind: TaskKind): BlogPermission | null {
  switch (kind) {
    case TaskKind.MediaRecompress:
      return BlogPermission.ManageSettings;
    case TaskKind.MediaStorageRecount:
    case TaskKind.Backup:
    case TaskKind.Restore:
      return null;
  }
}
